/**
 * The Carbon trend chart (DESIGN.md "Trend chart"): a recharts LineChart,
 * 260px by default, one 1.75px line per series in its category's mark color,
 * Secure Score dashed (4 3) because it is Microsoft's, not a pillar. Each line
 * ends in a 7px square and a 600 label in the category's ink tone. Gridlines
 * are border-subtle and axis labels 12px helper text; the key sits above.
 * Nulls are gaps (a week with no assessment), never zeros. Colors come from
 * getChartTheme() through useChartTheme, so they follow the theme toggle.
 *
 * A series can read against a second, right-hand axis (`rightAxis`, `series.axis`): percentages beside counts. The
 * right-axis lines are dashed, the key names each series' axis in words ("left axis", "right axis"), the end labels
 * give way to the axes, and a series' own format shows in its tick labels and the hover readout.
 */
import { useMemo, type ReactElement } from 'react'
import clsx from 'clsx'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, type TooltipProps } from 'recharts'
import { TONE_BG } from './tones'
import { mergeTrendRows } from './trendData'
import type { Category } from './types'
import { useChartTheme } from './useChartTheme'

export interface TrendPoint {
  x: string | number
  y: number | null
}

/** A pillar's category, or an uncategorized series: neutral (primary ink) or muted (helper ink). */
export type TrendTone = Category | 'neutral' | 'muted'

export interface TrendSeries {
  id: string
  label: string
  category: TrendTone
  points: TrendPoint[]
  /** Dashed line; defaults to true for the `secure` category and for any right-axis series. */
  dashed?: boolean
  /** Which y axis the series reads against; `right` needs the chart's `rightAxis`. Default `left`. */
  axis?: 'left' | 'right'
}

/** The right-hand y axis: its own range, ticks and value format. */
export interface TrendRightAxis {
  /** Axis range; defaults to the data's own range. */
  domain?: [number, number]
  /** Tick values; with a `domain` and no ticks, round steps inside it. */
  ticks?: number[]
  /** Tick labels, the hover readout and the series' values ("1.2k"). */
  format?: (value: number) => string
}

export interface TrendChartProps {
  series: TrendSeries[]
  height?: number
  /** Y-axis range; defaults to the data's own range. */
  yDomain?: [number, number]
  /** Y-axis gridlines and labels. With a `yDomain` and no ticks, round steps inside it (60, 70, 80, 90). */
  yTicks?: number[]
  /** Left-axis tick labels and the hover readout of the series on it (`${v}%`). */
  yFormat?: (value: number) => string
  /** A second y axis on the right for the series with `axis: 'right'`. Without it every series reads the left axis. */
  rightAxis?: TrendRightAxis
  /**
   * The x-axis order. Numbers and ISO dates sort on their own; pass this for label x values ("Jul 17") whose order
   * the series don't fully show. Without it, labels keep each series' own order (trendData.ts).
   */
  xDomain?: Array<string | number>
  /** Accessible name; defaults to each series' first and last values. */
  label?: string
  className?: string
}

interface DotProps {
  cx?: number
  cy?: number
  index?: number
  value?: number | null
}

// Round steps inside a domain, at most 5 of them: 55–90 gives 60, 70, 80, 90.
function roundTicks([lo, hi]: [number, number]): number[] | undefined {
  for (const step of [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000]) {
    const first = Math.ceil(lo / step) * step
    const count = Math.floor((hi - first) / step) + 1
    if (count <= 5) return Array.from({ length: count }, (_, i) => first + i * step)
  }
  return undefined
}

/**
 * The hover readout: a Carbon menu surface (layer-02, the menu shadow, `data-menu`) that renders only while
 * active, so no hidden tooltip box lingers in the page.
 */
function TrendTooltip({ active, payload, label, formats }: TooltipProps<number, string> & { formats?: Record<string, (value: number) => string> }) {
  if (!active || !payload?.length) return null
  return (
    <div data-menu="" className="min-w-40 border border-line-subtle bg-layer-2 px-3 py-2 text-helper text-text-secondary shadow-menu">
      <p className="mb-1 font-semibold text-text-primary">{label}</p>
      <ul className="m-0 list-none p-0">
        {payload
          .filter((p) => p.value != null)
          .map((p) => (
            <li key={String(p.dataKey)} className="flex items-center gap-2">
              <span aria-hidden="true" className="h-2.5 w-2.5 flex-none" style={{ background: p.color }} />
              <span className="min-w-0 flex-1">{p.name}</span>
              <b className="font-semibold tabular-nums text-text-primary">{formats?.[String(p.dataKey)]?.(p.value as number) ?? p.value}</b>
            </li>
          ))}
      </ul>
    </div>
  )
}

const firstLast = (s: TrendSeries) => {
  const ys = s.points.map((p) => p.y).filter((y): y is number => y != null)
  return ys.length ? `${s.label} ${ys[0]} to ${ys[ys.length - 1]}` : `${s.label} no data`
}

// Keys for the two uncategorized tones; a pillar category keeps its `--cat-*` color.
const TONE_LEGEND = {
  neutral: { dash: 'border-text-primary', swatch: 'bg-text-primary' },
  muted: { dash: 'border-text-helper', swatch: 'bg-text-helper' },
}

export function TrendChart({ series, height = 260, yDomain, yTicks, yFormat, rightAxis, xDomain, label, className }: TrendChartProps) {
  const theme = useChartTheme()
  // One row per x in axis order, so the end marker (lastIndex) labels each series' real last point.
  const data = useMemo(() => mergeTrendRows(series, xDomain), [series, xDomain])
  const lastIndex = (id: string) => {
    for (let i = data.length - 1; i >= 0; i--) if (data[i][id] != null) return i
    return -1
  }
  const dual = !!rightAxis
  const onRight = (s: TrendSeries) => dual && s.axis === 'right'
  const isDashed = (s: TrendSeries) => s.dashed ?? (s.category === 'secure' || onRight(s))
  const color = (s: TrendSeries) => (s.category === 'neutral' ? theme.neutral : s.category === 'muted' ? theme.axis : theme.series[s.category])
  const ink = (s: TrendSeries) => (s.category === 'neutral' ? theme.neutral : s.category === 'muted' ? theme.axis : theme.seriesInk[s.category])
  const format = (s: TrendSeries) => (onRight(s) ? rightAxis?.format : yFormat)
  const formats = Object.fromEntries(series.flatMap((s) => { const f = format(s); return f ? [[s.id, f]] : [] }))
  const axisWord = (s: TrendSeries) => (onRight(s) ? 'right axis' : 'left axis')

  return (
    <div className={clsx('min-w-0', className)}>
      <ul aria-hidden="true" className="m-0 flex list-none flex-wrap gap-x-4 gap-y-2 p-0 text-helper text-text-secondary">
        {series.map((s) => (
          <li key={s.id} className="inline-flex items-center gap-1.5">
            {isDashed(s) ? (
              <span
                className={clsx('h-0 w-3.5 border-t-2 border-dashed', s.category === 'neutral' || s.category === 'muted' ? TONE_LEGEND[s.category].dash : undefined)}
                style={s.category === 'neutral' || s.category === 'muted' ? undefined : { borderColor: `var(--cat-${s.category})` }}
              />
            ) : (
              <span className={clsx('h-2.5 w-2.5', s.category === 'neutral' || s.category === 'muted' ? TONE_LEGEND[s.category].swatch : TONE_BG[s.category])} />
            )}
            {s.label}
            {dual ? <span className="text-text-helper">· {axisWord(s)}</span> : null}
          </li>
        ))}
      </ul>
      <div role="img" aria-label={label ?? series.map((s) => (dual ? `${firstLast(s)} (${axisWord(s)})` : firstLast(s))).join(', ')} className="mt-4" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: dual ? 0 : 32, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={theme.grid} />
            <XAxis
              dataKey="x"
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              minTickGap={24}
              tick={{ fill: theme.axis, fontSize: 12 }}
            />
            <YAxis
              yAxisId="left"
              width={yFormat ? 40 : 28}
              tickFormatter={yFormat}
              domain={yDomain ?? ['auto', 'auto']}
              ticks={yTicks ?? (yDomain ? roundTicks(yDomain) : undefined)}
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              tick={{ fill: theme.axis, fontSize: 12 }}
            />
            {rightAxis ? (
              <YAxis
                yAxisId="right"
                orientation="right"
                width={40}
                domain={rightAxis.domain ?? ['auto', 'auto']}
                ticks={rightAxis.ticks ?? (rightAxis.domain ? roundTicks(rightAxis.domain) : undefined)}
                tickFormatter={rightAxis.format}
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fill: theme.axis, fontSize: 12 }}
              />
            ) : null}
            <Tooltip cursor={{ stroke: theme.grid }} isAnimationActive={false} content={<TrendTooltip formats={formats} />} />
            {series.map((s) => {
              const stroke = color(s)
              const end = lastIndex(s.id)
              const size = theme.markerSize
              const renderEnd = ({ cx, cy, index, value }: DotProps): ReactElement => {
                if (index !== end || cx == null || cy == null) return <g key={`${s.id}-dot-${index}`} />
                return (
                  <g key={`${s.id}-dot-${index}`}>
                    <rect x={cx - size / 2} y={cy - size / 2} width={size} height={size} fill={stroke} />
                    {dual ? null : (
                      <text x={cx + size / 2 + 4} y={cy + 4} fill={ink(s)} fontSize={12} fontWeight={600}>
                        {format(s)?.(value as number) ?? value}
                      </text>
                    )}
                  </g>
                )
              }
              return (
                <Line
                  key={s.id}
                  yAxisId={onRight(s) ? 'right' : 'left'}
                  type="linear"
                  dataKey={s.id}
                  name={s.label}
                  stroke={stroke}
                  strokeWidth={theme.strokeWidth}
                  strokeDasharray={isDashed(s) ? theme.dash.secure : undefined}
                  dot={renderEnd}
                  activeDot={({ cx, cy }: DotProps) => (
                    <rect x={(cx ?? 0) - size / 2} y={(cy ?? 0) - size / 2} width={size} height={size} fill={stroke} stroke={theme.activeDotFill} />
                  )}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default TrendChart
